/** Normalize API/ISO date strings to YYYY-MM-DD for form inputs. */
export function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  // Prefer calendar date prefix from ISO strings so timezone shifts cannot blank/shift DOB.
  const isoDatePrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDatePrefix) {
    const [y, m, d] = isoDatePrefix[1].split('-').map(n => parseInt(n, 10));
    if (!isValidCalendarDate(y, m, d)) return '';
    return isoDatePrefix[1];
  }
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True when y/m/d form a real calendar date (rejects 0000-00-00, Feb 30, etc.). */
export function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export function parseDateParts(value?: string): { day: string; month: string; year: string } {
  const normalized = toDateInputValue(value);
  if (!normalized) return { day: '', month: '', year: '' };
  const [year, month, day] = normalized.split('-');
  // Never surface invalid zero-padded parts (e.g. day "00") to selects
  if (day === '00' || month === '00' || year === '0000') {
    return { day: '', month: '', year: '' };
  }
  return { day, month, year };
}

/** Days in a calendar month (1–12). Uses a leap year when year is unknown so Feb can be 29. */
export function getDaysInMonth(month: number, year?: number): number {
  if (!month || month < 1 || month > 12) return 31;
  const y = year && year > 0 ? year : 2020;
  return new Date(y, month, 0).getDate();
}

export function getDaysInMonthForParts(month: string, year: string): number {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (isNaN(m) || m < 1 || m > 12) return 31;
  if (isNaN(y) || y < 1) return getDaysInMonth(m);
  return getDaysInMonth(m, y);
}

/** Clamp day string to the last valid day of the given month/year; empty stays empty. */
export function clampDayToMonth(day: string, month: string, year: string): string {
  if (!day) return '';
  const d = parseInt(day, 10);
  if (isNaN(d) || d < 1) return '';
  const max = getDaysInMonthForParts(month, year);
  const clamped = Math.min(d, max);
  return String(clamped).padStart(2, '0');
}

export type DateParts = { day: string; month: string; year: string };

/** True when at least one date part is selected. */
export function hasAnyDatePart(parts: DateParts): boolean {
  return !!(parts.day || parts.month || parts.year);
}

/** True when day fits in the given month/year (empty day is compatible). */
export function isDayCompatibleWithMonth(day: string, month: string, year: string): boolean {
  if (!day) return true;
  const d = parseInt(day, 10);
  if (isNaN(d) || d < 1) return false;
  return d <= getDaysInMonthForParts(month, year);
}

/** Build YYYY-MM-DD from day/month/year parts; returns '' if incomplete or invalid. */
export function buildDateFromParts(day: string, month: string, year: string): string {
  if (!day || !month || !year) return '';
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return '';
  if (!isValidCalendarDate(y, m, d)) return '';
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Year options newest-first. Default range: ~120 years ago through the current year. */
export function getYearOptions(minYear?: number, maxYear?: number): number[] {
  const currentYear = new Date().getFullYear();
  const max = maxYear ?? currentYear;
  const min = minYear ?? Math.max(1900, currentYear - 120);
  const years: number[] = [];
  for (let y = max; y >= min; y--) years.push(y);
  return years;
}
